import { FC, useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { Box, Button, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, VStack } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { fetcher, AdminAgentNewsEndpoints } from '@olagg/api-hooks'
import { ArticleCtaType } from '@olagg/db-types'
import { formArticleCtaSchema, FormArticleCtaData } from '@olagg/validation-schemas/articleCta.schemas'
import OlaggForm from '../../components/Forms/OlaggForm'
import OlaggFormInput from '../../components/Forms/OlaggFormInput'
import OlaggFormSelect from '../../components/Forms/OlaggFormSelect'
import ArtPreview from './ArtPreview'
import { useArtMeasurement } from './useArtMeasurement'
import {
  CONTENT_LANGS,
  PLACEHOLDER_HELP,
  deviceOptions,
  langLabels,
  langOptions,
  loggedInOptions,
  renderTypeOptions,
  slotOptions,
} from './constants'

interface IArticleCtaFormProps {
  articleCta?: ArticleCtaType
  onSubmit: (dataForm: FormArticleCtaData) => void
}

const emptyContent = () => ({
  title: '', subtitle: '', ctaLabel: '', imageUrl: '', imageAlt: ''
})

/** El multiselect trabaja con {value,label}; el backend guarda un array plano. */
const toOptions = (values?: string[] | null) =>
  (values || []).map(value => ({ value, label: value }))

/**
 * Pestañas de contenido por idioma, con la medición del arte y la preview del
 * recorte real del slot (R6.1). Las dimensiones son de sólo lectura: las llena
 * la medición y nadie las tipea.
 */
const ContentTabs: FC = () => {
  const { watch } = useFormContext()
  const { rulesFor } = useArtMeasurement()

  return (
    <Tabs variant='enclosed' colorScheme='purple' w='full' mb='5'>
      <TabList>
        {CONTENT_LANGS.map(lang => (
          <Tab key={lang}>{langLabels[lang]}</Tab>
        ))}
      </TabList>
      <TabPanels>
        {CONTENT_LANGS.map(lang => {
          const url = watch(`content.${lang}.imageUrl`) as string | undefined
          const { blocking, warnings, measurement, rendition } = rulesFor(lang, url)

          return (
            <TabPanel key={lang} px='0'>
              <OlaggFormInput
                label='Título'
                controlName={`content.${lang}.title`}
                description={`Admite placeholders. ${PLACEHOLDER_HELP}`}
                required={true}
              />
              <OlaggFormInput
                label='Subtítulo'
                controlName={`content.${lang}.subtitle`}
                description='Admite los mismos placeholders que el título'
              />
              <OlaggFormInput label='Texto del botón' controlName={`content.${lang}.ctaLabel`} required={true} />
              <OlaggFormInput
                label='URL de la imagen'
                controlName={`content.${lang}.imageUrl`}
                description='Se mide sola al pegarla. Admite {prize_image} y {product_image}: la imagen la resuelve el servidor y no se mide'
              />
              <OlaggFormInput
                label='Texto alternativo'
                controlName={`content.${lang}.imageAlt`}
                description='Si queda vacío la imagen se renderiza como decorativa (alt=""), nunca duplicando el título'
              />

              <ArtPreview
                url={url}
                measurement={measurement}
                rendition={rendition}
                blocking={blocking}
                warnings={warnings}
              />
            </TabPanel>
          )
        })}
      </TabPanels>
    </Tabs>
  )
}

const MeasuredDimensions: FC = () => {
  const { watch } = useFormContext()
  const width = watch('imageWidth')
  const height = watch('imageHeight')

  return (
    <Box mb='5'>
      <Text fontWeight={600} lineHeight='normal'>Dimensiones del archivo fuente</Text>
      <Text color='gray.400' fontSize='sm'>
        Las mide el formulario y no se editan. No son la geometría del &lt;img&gt;: esa sale de la tabla de rendición del slot.
      </Text>
      <Text mt='2'>{width && height ? `${width} × ${height}` : '—'}</Text>
    </Box>
  )
}

const ArticleCtaForm: FC<IArticleCtaFormProps> = ({ articleCta, onSubmit }) => {
  const [tagOptions, setTagOptions] = useState<{ value: string, label: string }[]>([])
  const [tagsFailed, setTagsFailed] = useState(false)

  const useFormMethods = useForm<FormArticleCtaData>({
    resolver: yupResolver(formArticleCtaSchema),
    mode: 'onChange',
    defaultValues: {
      key: articleCta?.key || '',
      slot: articleCta?.slot || 'in_body_mid',
      renderType: articleCta?.renderType || 'standard',
      variant: articleCta?.variant || 'A',
      experimentKey: articleCta?.experimentKey || '',
      weight: articleCta?.weight ?? 50,
      active: articleCta?.active ?? true,
      destination: articleCta?.destination || '',
      startsAt: articleCta?.startsAt?.slice(0, 16) || '',
      endsAt: articleCta?.endsAt?.slice(0, 16) || '',
      imageWidth: articleCta?.imageWidth ?? null,
      imageHeight: articleCta?.imageHeight ?? null,
      content: {
        es: { ...emptyContent(), ...articleCta?.content?.es },
        en: { ...emptyContent(), ...articleCta?.content?.en },
        pt: { ...emptyContent(), ...articleCta?.content?.pt },
      },
      targeting: {
        tags: toOptions(articleCta?.targeting?.tags) as any,
        lang: toOptions(articleCta?.targeting?.lang) as any,
        device: toOptions(articleCta?.targeting?.device) as any,
        loggedIn: articleCta?.targeting?.loggedIn === true
          ? 'true'
          : articleCta?.targeting?.loggedIn === false ? 'false' : 'any',
      },
    } as any
  })

  // El vocabulario de tags sale del catálogo, no de una lista escrita a mano.
  useEffect(() => {
    fetcher(AdminAgentNewsEndpoints.tags())
      .then(res => {
        setTagOptions((res?.tags || []).map((tag: string) => ({ value: tag, label: tag })))
        setTagsFailed(false)
      })
      // Que falle no puede ser silencioso: la lista vacía se ve igual que "no hay
      // ningún tag que coincida", y son dos problemas distintos.
      .catch(() => {
        setTagOptions([])
        setTagsFailed(true)
      })
  }, [])

  const renderType = useFormMethods.watch('renderType')

  return (
    <OlaggForm useFormMethods={useFormMethods} onSubmit={onSubmit} style={{ width: '100%' }}>
      <VStack color={'white'} w={'full'} maxW='700px' margin='0 auto'>
        <Box w='full'>
          <OlaggFormInput
            label='Key'
            controlName='key'
            description='Viaja como cta_id en los eventos y en las UTMs. Sin espacios. Ej.: cta_play_now'
            required={true}
          />

          <OlaggFormSelect
            label='Slot'
            controlName='slot'
            required={true}
            isSearchable={false}
            staticOptions={slotOptions}
            newVersion
          />

          <OlaggFormSelect
            label='Tipo de render'
            controlName='renderType'
            required={true}
            isSearchable={false}
            staticOptions={renderTypeOptions}
            newVersion
          />

          {renderType === 'poll' && (
            <Text fontSize='sm' color='orange.300' mb='5'>
              Las opciones no se cargan acá: se extraen de los códigos que ya están marcados en el
              cuerpo del artículo, y se revisan en la sección Encuestas de Novedades. Esta fila se
              muestra sólo en los artículos que tienen encuesta activa — en el resto, el slot cae al
              siguiente de la precedencia.
            </Text>
          )}

          <OlaggFormInput
            label='Variante'
            controlName='variant'
            description='A, B, … Identifica el brazo del experimento'
            required={true}
          />

          <OlaggFormInput
            label='Experimento'
            controlName='experimentKey'
            description='Vacío = activo siempre, sin experimento. Todos los brazos de un experimento van en el mismo slot'
          />

          <OlaggFormInput
            label='Peso'
            controlName='weight'
            description='0 a 100. Reparte el tráfico entre los brazos del experimento'
            required={true}
          />

          <OlaggFormInput
            label='Destino'
            controlName='destination'
            description='Ruta interna SIN prefijo de idioma (ej.: /mini-juegos/claw-ola), o una URL externa completa con https:// que abre en pestaña nueva y se sirve tal cual. Admite placeholders, ej.: /tienda/{product_slug}'
            required={true}
          />

          <OlaggFormInput
            label='Desde'
            controlName='startsAt'
            inputType='datetime'
            datePickerProps={{ backgroundColor: 'white' }}
          />

          <OlaggFormInput
            label='Hasta'
            controlName='endsAt'
            inputType='datetime'
            datePickerProps={{ backgroundColor: 'white' }}
          />

          <OlaggFormInput
            label='Activo'
            controlName='active'
            inputType='switch'
            description='Es el apagado rápido: sale del endpoint público en el siguiente request'
          />

          <Text fontSize='lg' fontWeight={700} mt='6' mb='2'>Contenido</Text>
          <ContentTabs />
          <MeasuredDimensions />

          <Text fontSize='lg' fontWeight={700} mt='6' mb='2'>Targeting</Text>
          <Text color='gray.400' fontSize='sm' mb='4'>Vacío significa sin filtro. El match de tags es por cualquiera, no por todos.</Text>

          <OlaggFormSelect
            label='Tags del artículo'
            controlName='targeting.tags'
            description={tagsFailed
              ? 'No se pudo cargar la lista de tags del catálogo. Guardá sin este filtro y avisá: es un error del endpoint, no que no haya tags.'
              : 'Se escriben igual que en el artículo. Sólo aparecen tags que existen en el catálogo.'}
            required={false}
            isSearchable={true}
            multiple
            staticOptions={tagOptions}
            newVersion
          />

          <OlaggFormSelect
            label='Idiomas'
            controlName='targeting.lang'
            required={false}
            isSearchable={false}
            multiple
            staticOptions={langOptions}
            newVersion
          />

          <OlaggFormSelect
            label='Devices'
            controlName='targeting.device'
            required={false}
            isSearchable={false}
            multiple
            staticOptions={deviceOptions}
            newVersion
          />

          <OlaggFormSelect
            label='Estado de login'
            controlName='targeting.loggedIn'
            required={false}
            isSearchable={false}
            staticOptions={loggedInOptions}
            newVersion
          />
        </Box>

        <Flex w='full' justifyContent='center'>
          <Button variant={'filledGradient'} type='submit' w={'full'} style={{ marginTop: '30px' }}>
            Guardar
          </Button>
        </Flex>
      </VStack>
    </OlaggForm>
  )
}

export default ArticleCtaForm
